import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import {
  Alert,
  Badge,
  Button,
  FormGroup,
  Input,
  Label,
  ListGroup,
  ListGroupItem,
  Spinner,
} from "reactstrap";

import {
  getHelpArticleUrl,
  HELP_ARTICLE_META_COMMERCE,
} from "../../helpers/helpCenter";
import {
  getMetaCommercePreview,
  getMetaCommerceStatus,
  MetaCommercePreview,
  MetaCommerceStatus,
  MetaCommerceSyncFilters,
  requestMetaCommerceSync,
} from "../../services/integrationService";
import { getCatalogCategories } from "../../services/catalogCategoryService";
import { CatalogCategory } from "../../types/catalogCategory";

interface MetaCommercePanelProps {
  enabled: boolean;
}

const formatWhen = (iso: string, locale: string): string => {
  try {
    return new Intl.DateTimeFormat(locale, {
      dateStyle: "short",
      timeStyle: "short",
    }).format(new Date(iso));
  } catch {
    return iso;
  }
};

const MetaCommercePanel: React.FC<MetaCommercePanelProps> = ({ enabled }) => {
  const { t, i18n } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [status, setStatus] = useState<MetaCommerceStatus | null>(null);
  const [preview, setPreview] = useState<MetaCommercePreview | null>(null);
  const [brand, setBrand] = useState("");
  const [categoryId, setCategoryId] = useState("");
  const [categories, setCategories] = useState<CatalogCategory[]>([]);

  const filters = useCallback((): MetaCommerceSyncFilters => {
    const payload: MetaCommerceSyncFilters = {};
    if (categoryId) {
      payload.category_id = Number(categoryId);
    }
    if (brand.trim()) {
      payload.brand = brand.trim();
    }
    return payload;
  }, [brand, categoryId]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const response = await getMetaCommerceStatus();
      setStatus(response.data);
    } catch {
      toast.error(t("studio.integrations.metaCommerce.statusLoadError"));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    void (async () => {
      try {
        const response = await getCatalogCategories({ per_page: 100 });
        setCategories(response.data ?? []);
      } catch {
        setCategories([]);
      }
    })();
  }, []);

  const handlePreview = async () => {
    setPreviewLoading(true);
    try {
      const response = await getMetaCommercePreview(filters());
      setPreview(response.data);
    } catch (error: unknown) {
      const message =
        (error as { response?: { data?: { error?: { message?: string } } } })
          ?.response?.data?.error?.message ||
        t("studio.integrations.metaCommerce.previewError");
      toast.error(message);
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleSync = async () => {
    setSyncing(true);
    try {
      await requestMetaCommerceSync(filters());
      toast.success(t("studio.integrations.metaCommerce.syncQueued"));
      await load();
      await handlePreview();
    } catch (error: unknown) {
      const message =
        (error as { response?: { data?: { error?: { message?: string } } } })
          ?.response?.data?.error?.message ||
        t("studio.integrations.metaCommerce.syncError");
      toast.error(message);
    } finally {
      setSyncing(false);
    }
  };

  if (loading) {
    return (
      <div className="text-center py-3">
        <Spinner size="sm" className="me-2" />
        {t("studio.integrations.metaCommerce.loading")}
      </div>
    );
  }

  const counts = status?.counts;
  const helpUrl = getHelpArticleUrl(HELP_ARTICLE_META_COMMERCE, i18n.language);
  const missingPhoto = counts?.missing_photo ?? 0;

  return (
    <div className="mt-3 border rounded p-3 bg-light">
      <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-3">
        <h6 className="mb-0">{t("studio.integrations.metaCommerce.panelTitle")}</h6>
        <div className="d-flex gap-2 flex-wrap">
          {status?.waba_ready ? (
            <Badge color="success">
              {t("studio.integrations.metaCommerce.wabaReady")}
            </Badge>
          ) : (
            <Badge color="warning">
              {t("studio.integrations.metaCommerce.wabaMissing")}
            </Badge>
          )}
          {status?.catalog_configured ? (
            <Badge color="success">
              {t("studio.integrations.metaCommerce.catalogReady")}
            </Badge>
          ) : (
            <Badge color="secondary">
              {t("studio.integrations.metaCommerce.catalogPending")}
            </Badge>
          )}
        </div>
      </div>

      {!status?.waba_ready && (
        <Alert color="warning" className="mb-3">
          {t("studio.integrations.metaCommerce.wabaHelp")}{" "}
          <a href={helpUrl} target="_blank" rel="noreferrer">
            {t("studio.integrations.metaCommerce.helpLink")}
          </a>
        </Alert>
      )}

      {missingPhoto > 0 && (
        <Alert color="info" className="mb-3">
          {t("studio.integrations.metaCommerce.missingPhotoBanner", {
            count: missingPhoto,
          })}{" "}
          <Link to="/products?catalog_missing_photo=1">
            {t("studio.integrations.metaCommerce.missingPhotoLink")}
          </Link>
        </Alert>
      )}

      {counts && (
        <p className="text-muted small mb-3">
          {t("studio.integrations.metaCommerce.countsSummary", {
            ok: counts.ok,
            failed: counts.failed,
            pending: counts.pending,
          })}
        </p>
      )}

      <div className="row g-2 mb-3">
        <div className="col-md-6">
          <FormGroup className="mb-0">
            <Label for="meta-sync-category" className="small">
              {t("studio.integrations.metaCommerce.filterCategory")}
            </Label>
            <Input
              id="meta-sync-category"
              type="select"
              value={categoryId}
              onChange={(event) => setCategoryId(event.target.value)}
            >
              <option value="">
                {t("studio.integrations.metaCommerce.filterCategoryAll")}
              </option>
              {categories.map((category) => (
                <option key={category.id} value={category.id}>
                  {category.name}
                </option>
              ))}
            </Input>
          </FormGroup>
        </div>
        <div className="col-md-6">
          <FormGroup className="mb-0">
            <Label for="meta-sync-brand" className="small">
              {t("studio.integrations.metaCommerce.filterBrand")}
            </Label>
            <Input
              id="meta-sync-brand"
              type="text"
              value={brand}
              onChange={(event) => setBrand(event.target.value)}
              placeholder={t(
                "studio.integrations.metaCommerce.filterBrandPlaceholder"
              )}
            />
          </FormGroup>
        </div>
      </div>

      {preview && (
        <Alert
          color={preview.exceeds_soft_cap ? "warning" : "secondary"}
          className="mb-3"
        >
          <div className="fw-medium mb-1">
            {t("studio.integrations.metaCommerce.previewTitle")}
          </div>
          <div className="small">
            {t("studio.integrations.metaCommerce.previewSummary", {
              total: preview.total,
              withPhoto: preview.with_photo,
              missingPhoto: preview.missing_photo,
            })}
          </div>
          {preview.sample.length > 0 && (
            <ul className="small mb-0 mt-2 ps-3">
              {preview.sample.map((item) => (
                <li key={item.id}>
                  {item.name}
                  {!item.has_photo
                    ? ` — ${t("studio.integrations.metaCommerce.previewNoPhoto")}`
                    : ""}
                </li>
              ))}
            </ul>
          )}
        </Alert>
      )}

      {status?.last_sync_at && (
        <p className="small mb-3">
          {t("studio.integrations.metaCommerce.lastSync", {
            when: formatWhen(status.last_sync_at, i18n.language),
          })}
        </p>
      )}

      <div className="d-flex flex-wrap gap-2 mb-3">
        <Button
          color="light"
          size="sm"
          disabled={!enabled || previewLoading || !status?.waba_ready}
          onClick={() => void handlePreview()}
        >
          {previewLoading ? (
            <>
              <Spinner size="sm" className="me-1" />
              {t("studio.integrations.metaCommerce.previewLoading")}
            </>
          ) : (
            t("studio.integrations.metaCommerce.previewButton")
          )}
        </Button>
        <Button
          color="primary"
          size="sm"
          disabled={
            !enabled ||
            syncing ||
            !status?.waba_ready ||
            Boolean(preview?.exceeds_soft_cap)
          }
          onClick={() => void handleSync()}
        >
          {syncing ? (
            <>
              <Spinner size="sm" className="me-1" />
              {t("studio.integrations.metaCommerce.syncing")}
            </>
          ) : (
            t("studio.integrations.metaCommerce.syncButton")
          )}
        </Button>
        <Button color="light" size="sm" onClick={() => void load()}>
          {t("studio.integrations.metaCommerce.refresh")}
        </Button>
      </div>

      <p className="text-muted small mb-2">
        {t("studio.integrations.metaCommerce.delayHint")}
      </p>

      {(status?.sync_history?.length ?? 0) > 0 && (
        <>
          <h6 className="mb-2">
            {t("studio.integrations.metaCommerce.historyTitle")}
          </h6>
          <ListGroup flush>
            {status!.sync_history.map((item, index) => (
              <ListGroupItem
                key={`${item.at}-${index}`}
                className="px-0 d-flex justify-content-between align-items-start"
              >
                <div>
                  <div className="small">{item.summary}</div>
                  <div className="text-muted" style={{ fontSize: "0.75rem" }}>
                    {formatWhen(item.at, i18n.language)}
                  </div>
                </div>
                <Badge color={item.ok ? "success" : "danger"} pill>
                  {item.ok
                    ? t("studio.integrations.metaCommerce.historyOk")
                    : t("studio.integrations.metaCommerce.historyFail")}
                </Badge>
              </ListGroupItem>
            ))}
          </ListGroup>
        </>
      )}
    </div>
  );
};

export default MetaCommercePanel;
